import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { discoverOdata } from "@/api/odata";
import {
  deleteMpReport,
  listMpReports,
  uploadMpReport,
} from "@/api/marketplace_reports";
import { useConnection } from "@/auth/ConnectionContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRuDate, formatRuDateTime } from "@/lib/format";
import { extractErrorMessage } from "@/lib/api-errors";
import type { OrgDTO } from "@/types/api";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

function StatusChip({ status }: { status: string }): React.ReactElement {
  const map: Record<string, { label: string; cls: string }> = {
    uploaded: { label: "Загружен", cls: "bg-slate-100 text-slate-700" },
    previewed: { label: "Готов к загрузке", cls: "bg-amber-100 text-amber-700" },
    created: { label: "Создан в 1С", cls: "bg-emerald-100 text-emerald-700" },
    error: { label: "Ошибка", cls: "bg-rose-100 text-rose-700" },
  };
  const v = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}
    >
      {v.label}
    </span>
  );
}

export function MarketplaceReportsPage(): React.ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [showAdd, setShowAdd] = useState(false);

  const reportsQ = useQuery({
    queryKey: ["mp-reports", connId],
    queryFn: listMpReports,
    enabled: !!connId,
  });

  const deleteMut = useMutation({
    mutationFn: deleteMpReport,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mp-reports", connId] });
      toast.success("Отчёт удалён локально (документы в 1С остались)");
    },
    onError: (err) => toast.error("Не удалось удалить", { description: String(err) }),
  });

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Отчёты МП</h1>
          <p className="text-sm text-muted-foreground">
            Загрузка XLSX-отчётов о реализации с маркетплейсов и создание
            «Отчётов комиссионера» в 1С УНФ.
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить отчёт
        </Button>
      </header>

      {showAdd && (
        <AddReportModal onClose={() => setShowAdd(false)} />
      )}

      {reportsQ.isLoading && (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      )}
      {reportsQ.isError && (
        <p className="text-sm text-rose-600">
          Не удалось загрузить список: {extractErrorMessage(reportsQ.error, "нет ответа")}
        </p>
      )}

      {reportsQ.data && reportsQ.data.length === 0 && (
        <div className="rounded-md border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Отчётов пока нет. Нажмите «Добавить отчёт».
          </p>
        </div>
      )}

      {reportsQ.data && reportsQ.data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Файл</TableHead>
              <TableHead>Организация</TableHead>
              <TableHead>МП</TableHead>
              <TableHead>Период</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="w-32">Загружен</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reportsQ.data.map((r) => (
              <TableRow
                key={r.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/mp-reports/${r.id}`)}
              >
                <TableCell className="max-w-md truncate" title={r.source_filename}>
                  {r.source_filename}
                </TableCell>
                <TableCell>{r.organization_name}</TableCell>
                <TableCell className="uppercase text-xs">{r.mp_code}</TableCell>
                <TableCell>
                  {r.period_start ? formatRuDate(r.period_start) : "—"}
                  {r.period_end ? ` … ${formatRuDate(r.period_end)}` : ""}
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                  {r.has_documents && (
                    <span className="ml-2 text-xs text-emerald-700">
                      ({r.documents.length})
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatRuDateTime(r.created_at)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Удалить отчёт локально? Документы в 1С останутся.")) {
                        deleteMut.mutate(r.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function AddReportModal({ onClose }: { onClose: () => void }): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { activeConnectionId: connId } = useConnection();
  const [organization, setOrganization] = useState<OrgDTO | null>(null);
  const [mpCode, setMpCode] = useState<"wb" | "ozon">("wb");
  const [file, setFile] = useState<File | null>(null);
  const [auxFile, setAuxFile] = useState<File | null>(null);

  const orgsQ = useQuery({
    queryKey: ["odata-orgs", connId],
    queryFn: async () => (await discoverOdata()).organizations,
    enabled: !!connId,
  });

  const uploadMut = useMutation({
    mutationFn: () =>
      uploadMpReport({
        file: file!,
        mp_code: mpCode,
        organization_key: organization!.ref_key,
        organization_name: organization!.description,
        aux_file: mpCode === "ozon" ? auxFile : null,
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["mp-reports", connId] });
      onClose();
      if (resp.duplicate_of) {
        toast.info("Файл уже был загружен раньше — открываем существующий отчёт");
      } else {
        toast.success("Отчёт загружен");
      }
      navigate(`/mp-reports/${resp.id}`);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        String(err);
      toast.error("Не удалось загрузить", { description: detail });
    },
  });

  const canSubmit =
    !!organization &&
    !!file &&
    (mpCode !== "ozon" || !!auxFile) &&
    !uploadMut.isPending;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Новый отчёт</DialogTitle>
          <DialogDescription>
            Загрузите еженедельный XLSX-отчёт о реализации с маркетплейса —
            мы создадим из него «Отчёт комиссионера» в 1С УНФ.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="org">Организация</Label>
            <select
              id="org"
              className={SELECT_CLASSES}
              value={organization?.ref_key ?? ""}
              onChange={(e) => {
                const o = orgsQ.data?.find((x) => x.ref_key === e.target.value);
                setOrganization(o ?? null);
              }}
              disabled={orgsQ.isLoading}
            >
              <option value="">
                {orgsQ.isLoading ? "Загрузка списка организаций…" : "— выберите —"}
              </option>
              {orgsQ.data?.map((o) => (
                <option key={o.ref_key} value={o.ref_key}>
                  {o.description}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mp">Маркетплейс</Label>
            <select
              id="mp"
              className={SELECT_CLASSES}
              value={mpCode}
              onChange={(e) => {
                const v = e.target.value as "wb" | "ozon";
                setMpCode(v);
                // При смене маркетплейса очищаем оба файла, чтобы не отправить
                // несовместимый набор (WB-отчёт под Ozon, и наоборот).
                setFile(null);
                setAuxFile(null);
              }}
            >
              <option value="wb">Wildberries</option>
              <option value="ozon">Озон</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Я.Маркет / Лету / Детский мир — позже.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="file">
              {mpCode === "ozon"
                ? "Позаказный отчёт о реализации (.xlsx)"
                : "Файл XLSX"}
            </Label>
            <Input
              id="file"
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} МБ
              </p>
            )}
          </div>

          {mpCode === "ozon" && (
            <div className="space-y-1.5">
              <Label htmlFor="aux_file">
                Реестр продаж юридическим лицам (.xlsx)
              </Label>
              <Input
                id="aux_file"
                type="file"
                accept=".xlsx"
                onChange={(e) => setAuxFile(e.target.files?.[0] ?? null)}
              />
              {auxFile && (
                <p className="text-xs text-muted-foreground">
                  {auxFile.name} · {(auxFile.size / 1024 / 1024).toFixed(1)} МБ
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Этот файл нужен, чтобы B2B-сделки попали отдельными строками в
                ТЧ «Покупатели» отчёта комиссионера.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!canSubmit} onClick={() => uploadMut.mutate()}>
            {uploadMut.isPending ? "Загрузка…" : "Загрузить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
